import { useEffect, useMemo, useReducer } from 'react';
import { useShowoff } from '@/features/Showoff';
import {
  Message,
  type MessagePosition,
} from '@/features/Showoff/components/Message';
import { CommonOverlay } from '@/features/Showoff/components/Overlay';
import { FOCUS_TYPE } from '@/features/Showoff/types';
import '@/features/Showoff/styles/showoff.scss';

import { useStore } from '@/store/useStore';

const RATIO_FOR_CHOOSING_SIDE = 1.4;

export const MessageOverlay = () => {
  const { elemRef, action, actionDismiss } = useShowoff();

  const gameLoadSuccess = useStore((s) => s.gameStatus.gameLoadSuccess);

  const [, forceRender] = useReducer((x: number) => x + 1, 0);

  useEffect(() => {
    const element = elemRef?.element;
    if (!element || !gameLoadSuccess) return;

    const observer = new ResizeObserver(() => forceRender());
    observer.observe(element);

    window.addEventListener('resize', forceRender);
    window.addEventListener('scroll', forceRender, true);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', forceRender);
      window.removeEventListener('scroll', forceRender, true);
    };
  }, [elemRef?.element, gameLoadSuccess]);

  const rect =
    gameLoadSuccess && elemRef?.element
      ? elemRef.element.getBoundingClientRect()
      : null;

  const offsetTop = rect?.y ?? null;
  const offsetLeft = rect?.x ?? null;
  const height = rect?.height ?? null;
  const width = rect?.width ?? null;

  const windowWidth = window.innerWidth;
  const windowHeight = window.innerHeight;

  const messageSizes = useMemo(() => {
    if (
      offsetTop === null ||
      offsetLeft === null ||
      height === null ||
      width === null
    ) {
      return {};
    }

    const marginToRight = windowWidth - (offsetLeft + width);
    const marginToBottom = windowHeight - (offsetTop + height);
    let sizes: MessagePosition = {};

    if (marginToRight * RATIO_FOR_CHOOSING_SIDE > offsetLeft) {
      sizes = { ...sizes, left: offsetLeft + width };
    } else {
      sizes = { ...sizes, right: windowWidth - offsetLeft };
    }

    if (marginToBottom * RATIO_FOR_CHOOSING_SIDE > offsetTop) {
      sizes = { ...sizes, top: offsetTop };
    } else {
      sizes = { ...sizes, bottom: windowHeight - offsetTop };
    }

    return sizes;
  }, [windowWidth, offsetLeft, width, windowHeight, offsetTop, height]);

  if (
    !gameLoadSuccess ||
    !elemRef?.element ||
    elemRef.focusType === FOCUS_TYPE.NOTHING
  ) {
    return null;
  }

  return (
    <div
      className="overlay-wrapper"
      style={{
        background:
          elemRef.focusType === FOCUS_TYPE.OVERLAY
            ? 'rgba(22, 39, 64, .6)'
            : '',
        zIndex: elemRef.focusType === FOCUS_TYPE.OVERLAY ? 3 : undefined,
      }}
      onClick={(e) => {
        e.preventDefault();
        actionDismiss();
      }}
    >
      <Message
        {...messageSizes}
        action={action}
        actionDismiss={actionDismiss}
      />
      {elemRef.focusType === FOCUS_TYPE.BOX &&
        offsetTop !== null &&
        offsetLeft !== null &&
        width !== null && (
          <CommonOverlay
            offsetTop={offsetTop}
            offsetLeft={offsetLeft}
            height={height ?? undefined}
            width={width}
          />
        )}
    </div>
  );
};
